import { useEffect, useState, type ReactNode } from 'react';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import { TextInput } from '../TextInput/TextInput';
import { Select, type SelectOption } from '../Select/Select';

export interface SuspendDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => void | Promise<void>;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** The exact string the user must type to enable the destructive button. */
  readonly confirmPhrase: string;
  /** Pre-canned reasons surfaced in a select for the audit log. */
  readonly reasons: readonly SelectOption[];
  /** Bulleted irreversible-consequence list shown above the form. */
  readonly consequences?: readonly ReactNode[];
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly loading?: boolean;
}

/**
 * Destructive-action dialog with type-to-confirm + mandatory reason picker
 * + visible consequence list. Used by F13 staff suspend, F13 affiliation end,
 * F12 sub-venue retire, and the admin b2b-account suspend flow.
 *
 * Type-to-confirm is exact-match (trimmed); the destructive button stays
 * disabled until both the phrase matches and a reason is selected.
 */
export function SuspendDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmPhrase,
  reasons,
  consequences,
  confirmLabel = 'Suspend',
  cancelLabel = 'Cancel',
  loading = false,
}: SuspendDialogProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState<string>(() => reasons[0]?.value ?? '');

  // Reset state when the dialog (re)opens. Intentional setState-in-effect:
  // this is the "form reset on dialog open" pattern. Alternative would be a
  // `key` prop on the Dialog to remount, but that changes the API surface.
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTyped('');
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReason(reasons[0]?.value ?? '');
    }
  }, [open, reasons]);

  const phraseMatches = typed.trim() === confirmPhrase;
  const canConfirm = phraseMatches && Boolean(reason) && !loading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    void onConfirm(reason);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {consequences && consequences.length > 0 ? (
          <div className="rounded-lg border border-danger/30 bg-danger/5 p-3">
            <p className="text-[10.5px] font-mono uppercase tracking-wider text-danger font-bold mb-2">
              What will happen
            </p>
            <ul className="list-disc ps-5 space-y-1 text-[13px] text-ink">
              {consequences.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-semibold text-ink">Reason (for audit log)</span>
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={reasons}
            aria-label="Suspension reason"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[12.5px] font-semibold text-ink">
            Type <span className="font-mono text-danger font-bold">{confirmPhrase}</span> to confirm
          </span>
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmPhrase}
            invalid={typed.length > 0 && !phraseMatches}
            autoComplete="off"
            aria-label="Confirmation phrase"
          />
        </label>
      </div>
    </Modal>
  );
}
